import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdExpandMore, MdCheck } from "react-icons/md";
import { GoDotFill } from "react-icons/go";
import "../styles/gameSelect.css";

/**
 * Picks one game from the running list.
 *
 * Screens show a row of tabs while there are a few games; past that the
 * row no longer fits, so they swap in this control. It is a plain
 * popover list - name, board size and round length per option - with
 * keyboard support (Enter / Space / arrows / Escape) and no dependency
 * on react-select so it matches the page styling exactly.
 *
 * @param {object[]} games       rows from /api/game-manage/active
 * @param {string}   value       selected game_code
 * @param {function} onChange    (game_code) => void
 * @param {string}   [suffix]    appended to the selected name, e.g. "Session"
 * @param {string}   [variant]   "light" (default) or "dark"
 */
export default function GameSelect({
  games = [],
  value,
  onChange,
  suffix = "",
  variant = "light",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => games.find((g) => g.game_code === value) || null,
    [games, value],
  );

  useEffect(() => {
    if (!open) return undefined;
    setCursor(Math.max(0, games.findIndex((g) => g.game_code === value)));
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, games, value]);

  useEffect(() => {
    if (!open || cursor < 0) return;
    const el = listRef.current?.children?.[cursor];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [open, cursor]);

  const choose = (code) => {
    if (code && code !== value) onChange?.(code);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(games.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      choose(games[cursor]?.game_code);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const label = selected
    ? `${selected.game_name}${suffix ? ` ${suffix}` : ""}`
    : "Select game";

  return (
    <div
      ref={rootRef}
      className={`gsel gsel--${variant}${open ? " is-open" : ""} ${className}`.trim()}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="gsel-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="gsel-trigger-dot" aria-hidden="true">
          <GoDotFill size={12} />
        </span>
        <span className="gsel-trigger-label">{label}</span>
        {selected && (
          <span className="gsel-trigger-meta">{selected.total_ghar} ghar</span>
        )}
        <MdExpandMore size={20} className="gsel-caret" />
      </button>

      {open && (
        <ul ref={listRef} className="gsel-menu" role="listbox" tabIndex={-1}>
          {games.map((g, i) => {
            const isSel = g.game_code === value;
            return (
              <li
                key={g.id ?? g.game_code}
                role="option"
                aria-selected={isSel}
                className={`gsel-item${isSel ? " is-selected" : ""}${
                  i === cursor ? " is-cursor" : ""
                }`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(g.game_code)}
              >
                <span className="gsel-item-name">
                  {g.game_name}
                  {suffix ? ` ${suffix}` : ""}
                </span>
                <span className="gsel-item-meta">
                  {g.total_ghar} ghar · {g.duration_minutes} min
                </span>
                <span className="gsel-item-live">Live</span>
                {isSel && <MdCheck size={18} className="gsel-item-check" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
